// access — one role/permission law for the UI (God 2026-09-24). Mirror of
// Algo-Engine/modules/access_control.py: keep PAGES identical in both files.
// The SERVER is the authority (/auth/me + per-endpoint checks); this file only decides
// what to draw, so a denied page is never even shown.
//   * MASTER_ADMIN = everything.
//   * Everyone else = only the pages/levels the Master granted; a missing page = denied.
//   * Role is a ceiling: live money (KALI) + system pages need ADMIN, VIEWER/ANALYST are
//     view-only, "IS BOSS" (approvals, user & permission changes) is Master-only.
export type Level = 'view' | 'action' | 'edit' | 'master';
export type PagePerm = Record<Level, boolean>;
export type Role = 'MASTER_ADMIN' | 'ADMIN' | 'TRADER' | 'ANALYST' | 'VIEWER' | 'CLIENT';

export const ROLE_RANK: Record<string, number> = { CLIENT: 0, VIEWER: 1, ANALYST: 2, TRADER: 3, ADMIN: 4, MASTER_ADMIN: 5 };
const ROLE_MAX_LEVEL: Record<string, Level> = { CLIENT: 'view', VIEWER: 'view', ANALYST: 'view', TRADER: 'edit', ADMIN: 'edit', MASTER_ADMIN: 'master' };
export const LEVELS: Level[] = ['view', 'action', 'edit', 'master'];

export type PageDef = { id: string; label: string; group: string; lane: '' | 'PAPER' | 'LIVE'; minRole: Role };
export const PAGES: PageDef[] = [
  { id: 'Dashboard',       label: 'Master Dashboard',              group: 'ANALYSIS', lane: '',      minRole: 'CLIENT' },
  { id: 'MamtaScreener',   label: 'Mamta Screener',                group: 'ANALYSIS', lane: '',      minRole: 'VIEWER' },
  { id: 'Watchlist',       label: 'Watchlist',                     group: 'ANALYSIS', lane: '',      minRole: 'CLIENT' },
  { id: 'OptionsChain',    label: 'Options Chain',                 group: 'ANALYSIS', lane: '',      minRole: 'VIEWER' },
  { id: 'PnlReports',      label: 'PnL Reports & Logs',            group: 'ANALYSIS', lane: '',      minRole: 'CLIENT' },
  { id: 'RnDAnalytics',    label: 'R&D Analytics',                 group: 'ANALYSIS', lane: '',      minRole: 'VIEWER' },
  { id: 'SuperChart',      label: 'Dumabati Super Chart',          group: 'PAPER',    lane: 'PAPER', minRole: 'VIEWER' },
  { id: 'LiveAutoTrading', label: 'Paper Auto Trading (DUMABATI)', group: 'PAPER',    lane: 'PAPER', minRole: 'VIEWER' },
  { id: 'PaperTrading',    label: 'Paper Trading',                 group: 'PAPER',    lane: 'PAPER', minRole: 'VIEWER' },
  { id: 'KaliSuperChart',  label: 'Kali Super Chart',              group: 'LIVE',     lane: 'LIVE',  minRole: 'ADMIN' },
  { id: 'Kali',            label: 'Live Auto Trading (KALI)',      group: 'LIVE',     lane: 'LIVE',  minRole: 'ADMIN' },
  { id: 'PositionBook',    label: 'Position Book',                 group: 'ORDERS',   lane: '',      minRole: 'CLIENT' },
  { id: 'OrderLine',       label: 'Order Line Engine',             group: 'ORDERS',   lane: '',      minRole: 'CLIENT' },
  { id: 'AccountsAdmin',   label: 'Accounts & Audit',              group: 'FINANCE',  lane: '',      minRole: 'ADMIN' },
  { id: 'MoneyManagement', label: 'Funds & Margin',                group: 'FINANCE',  lane: '',      minRole: 'CLIENT' },
  { id: 'ClientPortfolio', label: 'Client Fleet Portfolio',        group: 'FINANCE',  lane: '',      minRole: 'VIEWER' },
  { id: 'Pipeline',        label: 'God Approval / Pipeline',       group: 'SYSTEM',   lane: '',      minRole: 'ADMIN' },
  { id: 'MamtaAiPro',      label: 'Mamta AI Brain',                group: 'SYSTEM',   lane: '',      minRole: 'ADMIN' },
  { id: 'SymbolManager',   label: 'Symbol Manager',                group: 'SYSTEM',   lane: '',      minRole: 'ADMIN' },
  { id: 'ApiManagement',   label: 'Broker API Management',         group: 'SYSTEM',   lane: '',      minRole: 'ADMIN' },
  { id: 'UserControl',     label: 'User Control & Powers',         group: 'SYSTEM',   lane: '',      minRole: 'ADMIN' },
];

export const GROUPS: { key: string; title: string; color: string; note?: string }[] = [
  { key: 'ANALYSIS', title: '📊 ANALYSIS', color: '#818cf8' },
  { key: 'PAPER',    title: '🧪 PAPER TRADING · DUMABATI', color: '#38bdf8', note: 'virtual money' },
  { key: 'LIVE',     title: '🔴 LIVE TRADING · KALI · REAL MONEY', color: '#f87171', note: 'sirf ADMIN role ko diya ja sakta hai' },
  { key: 'ORDERS',   title: '📒 ORDERS & POSITIONS', color: '#fbbf24' },
  { key: 'FINANCE',  title: '💰 FINANCE', color: '#10b981' },
  { key: 'SYSTEM',   title: '👑 SYSTEM & ADMIN', color: '#c084fc', note: 'sirf ADMIN role ko diya ja sakta hai' },
];

export const NONE: PagePerm = { view: false, action: false, edit: false, master: false };

export function effectivePerms(role: string, stored: Record<string, Partial<PagePerm>> | null | undefined): Record<string, PagePerm> {
  const r = String(role || '').toUpperCase();
  const out: Record<string, PagePerm> = {};
  if (r === 'MASTER_ADMIN') { PAGES.forEach(p => { out[p.id] = { view: true, action: true, edit: true, master: true }; }); return out; }
  const cap = LEVELS.indexOf(ROLE_MAX_LEVEL[r] || 'view');
  PAGES.forEach(p => {
    const s = (stored && stored[p.id]) || {};
    const allowed = (ROLE_RANK[r] ?? 0) >= (ROLE_RANK[p.minRole] ?? 0);
    const e = {} as PagePerm;
    LEVELS.forEach((lv, i) => { e[lv] = !!(allowed && s[lv] && i <= cap); });
    out[p.id] = e.view ? e : { ...NONE };
  });
  return out;
}

/** Why a level is locked for a role (null = grantable). Used by User Control. */
export function lockReason(role: string, page: PageDef, lv: Level): string | null {
  const r = String(role || '').toUpperCase();
  if (r === 'MASTER_ADMIN') return null;
  if ((ROLE_RANK[r] ?? 0) < (ROLE_RANK[page.minRole] ?? 0)) return `${page.minRole} role chahiye`;
  if (lv === 'master') return 'sirf Master Admin (RULE #17)';
  const cap = LEVELS.indexOf(ROLE_MAX_LEVEL[r] || 'view');
  if (LEVELS.indexOf(lv) > cap) return `${r} sirf dekh sakta hai`;
  return null;
}

/** Role templates: a starting point the Master can edit, never a grant above the ceiling. */
export function roleTemplate(role: string): Record<string, PagePerm> {
  const r = String(role || '').toUpperCase();
  const p: Record<string, PagePerm> = {};
  PAGES.forEach(pg => { p[pg.id] = { ...NONE }; });
  const set = (ids: string[], v: Partial<PagePerm>) => ids.forEach(id => { p[id] = { ...NONE, ...v }; });
  if (r === 'MASTER_ADMIN') PAGES.forEach(pg => { p[pg.id] = { view: true, action: true, edit: true, master: true }; });
  else if (r === 'ADMIN') {
    // everything except live money + user/permission control: those the Master ticks by hand
    set(PAGES.filter(pg => pg.lane !== 'LIVE' && pg.id !== 'UserControl').map(pg => pg.id), { view: true, action: true, edit: true });
  } else if (r === 'TRADER') {
    set(['Dashboard', 'Watchlist', 'OptionsChain', 'MamtaScreener', 'PnlReports', 'SuperChart'], { view: true });
    set(['LiveAutoTrading', 'PaperTrading', 'PositionBook', 'OrderLine'], { view: true, action: true });
  } else if (r === 'ANALYST') {
    set(['Dashboard', 'Watchlist', 'OptionsChain', 'MamtaScreener', 'SuperChart', 'PnlReports', 'RnDAnalytics'], { view: true });
  } else if (r === 'VIEWER') {
    set(['Dashboard', 'Watchlist', 'PnlReports', 'PositionBook'], { view: true });
  }
  // a template never exceeds the role ceiling
  const eff = effectivePerms(r, p);
  return r === 'MASTER_ADMIN' ? p : eff;
}

/** Server truth for the logged-in caller. null = not authenticated / unreachable. */
export async function fetchMe(api: string): Promise<any | null> {
  const h: Record<string, string> = {};
  try {
    const b = localStorage.getItem('jc_auth_token') || '';
    if (b) h['Authorization'] = `Bearer ${b}`;
  } catch { /* storage blocked */ }
  try {
    const r = await fetch(`${api}/api/auth/me`, { headers: h, credentials: 'include', signal: AbortSignal.timeout(15000) });
    if (!r.ok) return null;
    const j = await r.json();
    return j && j.authenticated ? j : { authenticated: false };
  } catch { return null; }
}
